import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Spinner from "features/spinner/Spinner";
import { useOrderContext } from "context/OrderContext";
import { createPdf, IMAGE_QUALITY, ImageQuality } from "features/final-report/createPdf";
import LargeDialog from "components/LargeDialog";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import { LANGUAGE, Language } from "features/final-report/localization/types";
import { useExcludeChecklistMutation } from "app/api/orders";
import { UserRole } from "features/auth/types/user";
import { Checklist } from "models/Checklist";
import { useAuthContext } from "features/auth/AuthContext";

type ExportFinalReportDialogProps = {
  onClose: () => void;
};

const ExportFinalReportDialog: React.FC<ExportFinalReportDialogProps> = ({ onClose }) => {
  const order = useOrderContext();
  const { user } = useAuthContext();
  const [imageQuality, setImageQuality] = useState<ImageQuality>(IMAGE_QUALITY.LOW);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(LANGUAGE.SV);

  const [excludeChecklist] = useExcludeChecklistMutation();

  const userCanExcludeChecklists =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.USER_ADMIN ||
    user?.role === UserRole.SALES;

  const toggleExcludeChecklist = (checklist: Checklist) => {
    if (!checklist.id) return;
    excludeChecklist({
      orderId: order.ordernr,
      checklistId: checklist.id,
      excludedFromReport: !checklist?.excludedFromReport,
    });
  };

  const handleDownload = async () => {
    setIsDownloading(true);

    const blob = await createPdf(order, imageQuality, selectedLanguage).catch((error) => {
      console.error(error);
      setDownloadError("Något gick fel när rapporten hämtades");
      setIsDownloading(false);
      return null;
    });

    if (!blob) {
      console.error("Failed to create PDF blob");
      setDownloadError("Något gick fel när rapporten hämtades");
      setIsDownloading(false);
      return;
    }

    const fileUrl = window.URL.createObjectURL(blob);

    const element = document.createElement("a");
    element.href = fileUrl;
    // The filename
    element.download =
      selectedLanguage === LANGUAGE.SV
        ? `${order.ordernr} - Slutrapport.pdf`
        : `${order.ordernr} - Final Report.pdf`;
    element.click();

    window.URL.revokeObjectURL(fileUrl);
    setIsDownloading(false);
  };

  return (
    <LargeDialog
      onClose={onClose}
      title={`Exportera slutrapport för order ${order.ordernr}`}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isDownloading}
            startIcon={<ClearIcon />}
          >
            Avbryt
          </Button>

          <Button
            variant="contained"
            loading={isDownloading}
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
          >
            Hämta
          </Button>
        </>
      }
    >
      {!isDownloading && (
        <Box>
          <Box display="grid">
            <FormLabel sx={{ m: 0 }}>Checklistor att inkludera</FormLabel>
            {order.checklists.map((checklist) => {
              return (
                <FormControlLabel
                  label={checklist.roomName}
                  control={
                    <Checkbox
                      disabled={!userCanExcludeChecklists}
                      checked={!checklist.excludedFromReport}
                      onChange={() => toggleExcludeChecklist(checklist)}
                    />
                  }
                />
              );
            })}
          </Box>

          <FormControl fullWidth margin="normal">
            <FormLabel sx={{ m: 0, alignItems: "center", display: "flex" }}>
              Bildkvalitet
              <Tooltip
                arrow
                title="En högre bildkvalitet drar upp filstorleken. För att undvika för stora PDF-filer rekommenderas i vanliga fall därför låg bildkvalitet."
              >
                <HelpOutlineOutlined sx={{ opacity: 0.4, marginLeft: 1 }} />
              </Tooltip>
            </FormLabel>

            <FormControl>
              <RadioGroup
                row
                defaultValue={IMAGE_QUALITY.LOW}
                value={imageQuality}
                onChange={(e) => setImageQuality(e.target.value as ImageQuality)}
              >
                <FormControlLabel value={IMAGE_QUALITY.LOW} control={<Radio />} label="Låg" />
                <FormControlLabel value={IMAGE_QUALITY.MEDIUM} control={<Radio />} label="Medium" />
                <FormControlLabel value={IMAGE_QUALITY.HIGH} control={<Radio />} label="Hög" />
              </RadioGroup>
            </FormControl>
          </FormControl>

          <TextField
            select
            label="Språk"
            variant="filled"
            margin="normal"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value as Language)}
            sx={{ width: "50%", minWidth: 200 }}
          >
            <MenuItem value={LANGUAGE.SV}>Svenska</MenuItem>
            <MenuItem value={LANGUAGE.EN}>Engelska</MenuItem>
          </TextField>
        </Box>
      )}

      {downloadError && <Alert severity="error">{downloadError}</Alert>}

      {isDownloading && (
        <Box sx={{ display: "grid", placeItems: "center", my: 5, gap: 2 }}>
          <Spinner color="grey" size="4rem" />
          <Typography>Rapporten laddas...</Typography>
        </Box>
      )}
    </LargeDialog>
  );
};

export default ExportFinalReportDialog;
